'use client';

/**
 * Wiki sidebar header
 *
 * Repo identity plus wiki-level state and actions at the top of the wiki tree
 * sidebar. Freshness, version and automation are properties of the wiki as a
 * whole (one generation = one version), so they live beside the repo name
 * rather than beside the current page's table of contents.
 *
 * Owns:
 * - the freshness line (or a "viewing an older version" notice), which also
 *   reports a refresh running or failed behind the version shown
 * - an attention notice for repo sync (only when actionable)
 * - the overflow menu: regenerate, share/export, integrations, version
 *   history, settings and delete
 * - the wiki settings dialog (including auto-refresh and repo sync) and the
 *   upgrade modal
 */
import { c as _c } from "react/compiler-runtime";
import type { WikiVersion } from '@/components/wiki/wiki-version-menu-items';
import { differenceInDays } from 'date-fns';
import { AlertCircle, Copy, Download, ExternalLink, GitPullRequest, Globe, History, Loader2, MoreVertical, RefreshCw, RotateCcw, Settings } from 'lucide-react';
import Link from 'next/link';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';
import { CursorLogo } from '@/components/icons/cursor-logo';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { BetterTooltip } from '@/components/ui/tooltip';
import { WikiDeleteMenuItem } from '@/components/wiki/wiki-delete-menu-item';
import { exportWikiMarkdown } from '@/components/wiki/wiki-export';
import { getWikiGenerationAccessState } from '@/components/wiki/wiki-generation';
import { getWikiRepoSyncNotice } from '@/components/wiki/wiki-repo-sync-notice';
import { WikiSettingsDialog } from '@/components/wiki/wiki-settings-dialog';
import { WikiVersionMenuItems } from '@/components/wiki/wiki-version-menu-items';
import { usePrivateWikiSlots } from '@/data/wiki/use-private-wiki-slots';
import { useLiveWikiSettings } from '@/data/wiki/use-wiki-settings';
import { SETTINGS_TABS } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { formatExactTimestamp, formatShortDate, getFromNow, getShortRelativeTime } from '@/lib/utils/date-formatters';
import { buildSettingsPath } from '@/utils/app-paths';
import { buildWikiPath } from '@/utils/wiki-paths';
import { getDefaultWikiVersionNumber } from '@/utils/wiki-status';
type WikiSidebarHeaderProps = {
  installationId: number | null;
  repositoryId: number | null;
  lastUpdatedAt?: string;
  /** Wiki metadata still fetching; keeps the freshness placeholder up. */
  isWikiLoading?: boolean;
  isRefreshing?: boolean;
  /** The newest refresh failed while an older completed version is shown. */
  isLastRefreshFailed: boolean;
  onRefresh?: () => void | Promise<void>;
  selectedVersion?: number | null;
  wikiVersions?: WikiVersion[];
  hasExportableContent?: boolean;
  wikiLanguage?: string | null;
  isPublicRepo?: boolean;
  owner: string;
  repo: string;
};

/**
 * Plain repo identity for the public wiki view, which has no wiki-level
 * actions to offer.
 */
export function WikiRepoLabel(t0) {
  const $ = _c(3);
  const {
    owner,
    repo
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="text-xs text-muted-foreground mb-1">Repository</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `${owner}/${repo}`;
  let t3;
  if ($[1] !== t2) {
    t3 = <div className="px-3 py-2">{t1}<div className="text-sm font-medium text-foreground wrap-break-word">{t2}</div></div>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
export function WikiSidebarHeader({
  installationId,
  repositoryId,
  lastUpdatedAt,
  isWikiLoading = false,
  isRefreshing = false,
  isLastRefreshFailed,
  onRefresh,
  selectedVersion,
  wikiVersions,
  hasExportableContent = false,
  wikiLanguage,
  isPublicRepo = false,
  owner,
  repo
}: WikiSidebarHeaderProps) {
  const router = useRouter();
  const params = useParams();
  const searchParams = useSearchParams();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const {
    settings,
    isLoading,
    isPersisting,
    updateSettings,
    syncNow
  } = useLiveWikiSettings({
    installationId,
    repositoryId
  });
  const {
    data: privateWikiSlots
  } = usePrivateWikiSlots(showSettingsModal && !isPublicRepo ? installationId : null);
  const firstGeneratedAt = privateWikiSlots?.slots.find(slot => slot.repositoryId === repositoryId && !slot.deletedAt)?.firstGeneratedAt;
  const daysSinceGeneration = firstGeneratedAt ? Math.max(0, differenceInDays(new Date(), new Date(firstGeneratedAt))) : null;
  const repoSyncAvailable = settings?.repoSyncAvailable ?? false;
  const repoSyncEnabled = repoSyncAvailable && (settings?.repoSyncEnabled ?? false);
  const wikiGenerationAccess = getWikiGenerationAccessState({
    isPublicRepo,
    hasActiveSubscription: settings?.hasActiveSubscription,
    isSettingsLoading: isLoading
  });
  const canManualRefreshAt = settings?.canManualRefreshAt ? new Date(settings.canManualRefreshAt) : null;
  const canRefreshNow = !canManualRefreshAt || canManualRefreshAt <= new Date();
  const refreshCooldownMessage = canManualRefreshAt ? `Refresh available ${getFromNow(canManualRefreshAt)}` : null;
  const getRefreshActionLabel = () => {
    if (!wikiGenerationAccess.hasKnownAccess) {
      return 'Checking refresh access...';
    }
    if (!wikiGenerationAccess.hasAccess) {
      return 'Upgrade to refresh private wiki';
    }
    if (canRefreshNow) {
      return 'Regenerate wiki';
    }
    return refreshCooldownMessage || 'Regenerate wiki';
  };

  // Version currently being viewed, and whether it is behind the latest one.
  const versionFromPath = params?.version as string | undefined;
  const parsedPathVersion = versionFromPath ? Number(versionFromPath) : null;
  const currentPathVersion = parsedPathVersion && !Number.isNaN(parsedPathVersion) ? parsedPathVersion : null;
  // The version the unversioned wiki URL serves: the newest completed one.
  const latestVersionNumber = getDefaultWikiVersionNumber(wikiVersions);
  const viewedVersion = selectedVersion ?? currentPathVersion;
  const isViewingOlderVersion = viewedVersion !== null && Boolean(wikiVersions?.length) && viewedVersion !== latestVersionNumber;
  const viewedVersionDate = wikiVersions?.find(version => version.version === viewedVersion)?.created_at;
  const repoSyncNotice = repoSyncEnabled ? getWikiRepoSyncNotice(settings?.latestRepoSyncRun ?? null, {
    installationId,
    owner,
    repo
  }) : null;
  const handleRefreshClick = async () => {
    if (!wikiGenerationAccess.hasKnownAccess) {
      return;
    }
    if (!wikiGenerationAccess.hasAccess) {
      setShowUpgradeModal(true);
      return;
    }
    if (isRefreshing || isRedirecting) {
      toast.info('Refresh already in progress, please wait...');
      return;
    }
    setIsRedirecting(true);
    try {
      if (onRefresh) {
        await onRefresh();
      }
      if (selectedVersion !== null) {
        setTimeout(() => {
          const path = buildWikiPath({
            owner,
            repo,
            isPublicView: false
          });
          router.push(path);
          setIsRedirecting(false);
        }, 500);
      } else {
        setIsRedirecting(false);
      }
    } catch {
      toast.error('Failed to refresh wiki');
      setIsRedirecting(false);
    }
  };
  const handleExportClick = async () => {
    if (isExporting || !hasExportableContent) {
      return;
    }
    setIsExporting(true);
    try {
      await exportWikiMarkdown({
        owner,
        repo,
        version: selectedVersion,
        language: wikiLanguage
      });
      toast.success('Wiki exported as markdown');
    } catch (error) {
      console.error('[WikiSidebarHeader] Failed to export wiki', error);
      toast.error('Unable to export wiki', {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setIsExporting(false);
    }
  };
  const handleUpgrade = () => {
    window.open(buildSettingsPath({
      tab: SETTINGS_TABS.BILLING
    }), '_blank', 'noopener,noreferrer');
  };
  const handleConnectCursor = () => {
    router.push(buildSettingsPath({
      integration: 'mcp'
    }));
  };
  const copyWikiLink = async (isPublicView: boolean) => {
    const path_0 = buildWikiPath({
      owner,
      repo,
      // The public view always serves the latest version.
      version: !isPublicView && selectedVersion ? String(selectedVersion) : undefined,
      page: searchParams.get('page') || undefined,
      isPublicView
    });
    const url = `${window.location.origin}${path_0}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(isPublicView ? 'Public link copied to clipboard' : 'Link copied to clipboard');
    } catch {
      toast.error('Failed to copy link');
    }
  };
  const hasWikiTarget = Boolean(installationId && repositoryId);
  const isBusy = isRefreshing || isRedirecting;
  // Retry only when it would start a refresh; the menu covers upgrades and cooldowns.
  const canRetryRefresh = hasWikiTarget && Boolean(onRefresh) && wikiGenerationAccess.hasKnownAccess && wikiGenerationAccess.hasAccess && canRefreshNow;
  const renderFreshnessLine = () => {
    if (isBusy) {
      return <p className="text-xs text-muted-foreground">Refreshing wiki…</p>;
    }
    if (isLastRefreshFailed) {
      return <div className="flex min-w-0 items-center gap-x-2 text-xs text-muted-foreground">
          <span className="truncate">Last refresh failed</span>
          {canRetryRefresh && <button type="button" onClick={() => void handleRefreshClick()} className="shrink-0 underline underline-offset-2 hover:text-foreground">
              Retry
            </button>}
        </div>;
    }
    if (lastUpdatedAt) {
      return <BetterTooltip content={formatExactTimestamp(lastUpdatedAt)} side="bottom">
          <p className="text-xs text-muted-foreground truncate cursor-default">
            {(() => {
            const relativeTime = getShortRelativeTime(lastUpdatedAt);
            return relativeTime === 'now' ? 'Updated just now' : `Updated ${relativeTime} ago`;
          })()}
          </p>
        </BetterTooltip>;
    }

    // Settings or wiki metadata are still in flight; a bare "Not indexed yet"
    // would flash the wrong state for an indexed wiki.
    if ((isLoading || isWikiLoading) && hasWikiTarget) {
      return <Skeleton className="h-3 w-24 rounded" />;
    }
    return <p className="text-xs text-muted-foreground">Not indexed yet</p>;
  };
  return <>
      <div className="px-3 py-2 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 text-sm font-medium text-foreground wrap-break-word">
            {`${owner}/${repo}`}
          </div>

          {hasWikiTarget && <DropdownMenu>
              <BetterTooltip content="Wiki actions" side="bottom">
                <div>
                  <DropdownMenuTrigger asChild>
                    <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-muted-foreground hover:text-white">
                      {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MoreVertical className="h-3 w-3" />}
                      <span className="sr-only">Wiki actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                </div>
              </BetterTooltip>
              <DropdownMenuContent align="end" className="min-w-48">
                <DropdownMenuItem onClick={handleRefreshClick} disabled={isRefreshing || isRedirecting || !wikiGenerationAccess.hasKnownAccess || !onRefresh || wikiGenerationAccess.hasAccess && !canRefreshNow}>
                  <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                  {getRefreshActionLabel()}
                </DropdownMenuItem>
                {repoSyncEnabled && <DropdownMenuItem onClick={() => void syncNow()} disabled={isPersisting}>
                    <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
                    Sync to repo now
                  </DropdownMenuItem>}

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => void copyWikiLink(false)}>
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                  Copy link
                </DropdownMenuItem>
                {isPublicRepo && <DropdownMenuItem onClick={() => void copyWikiLink(true)}>
                    <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                    Copy public link
                  </DropdownMenuItem>}
                <DropdownMenuItem onClick={handleExportClick} disabled={isExporting || !hasExportableContent}>
                  {isExporting ? <Loader2 className="h-3.5 w-3.5 text-muted-foreground animate-spin" /> : <Download className="h-3.5 w-3.5 text-muted-foreground" />}
                  Export markdown
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={handleConnectCursor}>
                  <CursorLogo className="h-3.5 w-3.5 text-muted-foreground" />
                  Connect to Cursor
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push(buildSettingsPath({
              tab: 'integrations'
            }))}>
                  <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                  Connect wiki MCP
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {wikiVersions && wikiVersions.length > 0 && <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <History className="h-3.5 w-3.5 text-muted-foreground" />
                      Version history
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-[400px] overflow-y-auto custom-scrollbar">
                      <WikiVersionMenuItems owner={owner} repo={repo} wikiVersions={wikiVersions} viewedVersion={viewedVersion} latestVersionNumber={latestVersionNumber} />
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>}
                <DropdownMenuItem onClick={() => setShowSettingsModal(true)}>
                  <Settings className="h-3.5 w-3.5 text-muted-foreground" />
                  Wiki settings
                </DropdownMenuItem>
                {!isPublicRepo && installationId && repositoryId && <WikiDeleteMenuItem installationId={installationId} repositoryId={repositoryId} />}
              </DropdownMenuContent>
            </DropdownMenu>}
        </div>

        {isViewingOlderVersion ? <OlderVersionNotice owner={owner} repo={repo} viewedVersion={viewedVersion} viewedVersionDate={viewedVersionDate} page={searchParams.get('page')} /> : renderFreshnessLine()}

        {/* Plain element (not a component) so the tooltip's Slot can merge its ref
            and pointer handlers into the trigger. */}
        {repoSyncNotice && (repoSyncNotice.tooltip ? <BetterTooltip content={repoSyncNotice.tooltip} side="bottom">
              {renderRepoSyncNoticeLine(repoSyncNotice)}
            </BetterTooltip> : renderRepoSyncNoticeLine(repoSyncNotice))}
      </div>

      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="space-y-3">
            <DialogTitle>Upgrade wiki access</DialogTitle>
            <DialogDescription className="text-base">
              Automatic wiki refresh and private wiki generation are only available on paid plans.
              Upgrade now to keep your docs up to date.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>
              Maybe later
            </Button>
            <Button onClick={handleUpgrade}>
              View plans
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mounted only while open so the inputs seed from the latest settings. */}
      {showSettingsModal && <WikiSettingsDialog onClose={() => setShowSettingsModal(false)} installationId={installationId} repositoryId={repositoryId} settings={settings} isLoading={isLoading} isPersisting={isPersisting} updateSettings={updateSettings} firstGeneratedAt={firstGeneratedAt} daysSinceGeneration={daysSinceGeneration} onUpgradeRequired={() => {
      // Both dialogs share the same overlay layer; the later-mounted
      // settings dialog would cover the upgrade prompt.
      setShowSettingsModal(false);
      setShowUpgradeModal(true);
    }} />}
    </>;
}
function OlderVersionNotice(t0) {
  const $ = _c(13);
  const {
    owner,
    repo,
    viewedVersion,
    viewedVersionDate,
    page
  } = t0;
  let t1;
  if ($[0] !== viewedVersionDate) {
    t1 = viewedVersionDate ? ` · ${formatShortDate(viewedVersionDate)}` : "";
    $[0] = viewedVersionDate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `Viewing v${viewedVersion}${t1}`;
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="truncate">{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = page || undefined;
  let t5;
  if ($[4] !== owner || $[5] !== repo || $[6] !== t4) {
    t5 = buildWikiPath({
      owner,
      repo,
      page: t4,
      isPublicView: false
    });
    $[4] = owner;
    $[5] = repo;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <Link href={t5} className="shrink-0 underline underline-offset-2 hover:text-foreground">Back to latest</Link>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t6) {
    t7 = <div className="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-muted-foreground">{t3}{t6}</div>;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
type RepoSyncNotice = NonNullable<ReturnType<typeof getWikiRepoSyncNotice>>;
function renderRepoSyncNoticeLine(notice: RepoSyncNotice) {
  const Icon = notice.isError ? AlertCircle : GitPullRequest;
  return <div className={cn('flex min-w-0 items-center gap-1.5 text-xs', notice.isError ? 'text-destructive' : 'text-muted-foreground')}>
      <Icon className="h-3 w-3 shrink-0" />
      {notice.href ? <a href={notice.href} target="_blank" rel="noreferrer" className={cn('inline-flex min-w-0 items-center gap-1', notice.isError ? 'hover:text-destructive/80' : 'hover:text-foreground')}>
          <span className="truncate">{notice.text}</span>
          <ExternalLink className="h-3 w-3 shrink-0" />
        </a> : <span className="truncate">{notice.text}</span>}
    </div>;
}